import { AnimatePresence, HTMLMotionProps, motion, useMotionValue } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { classes } from '../../../../utils/commonUtils';
import css from './season-selector.module.scss';

interface SeasonSelectorProps {
  seasons: number[];
  activeSeason: number;
  setActiveSeason: (season: number) => void;
}

export function SeasonSelector({ seasons, activeSeason, setActiveSeason }: SeasonSelectorProps) {
  const { t } = useTranslation();

  return (
    <nav className={css.container}>
      <h3 className={css.label}>{t('common:seasons-label')}</h3>
      <SeasonsList seasons={seasons} activeSeason={activeSeason} setActiveSeason={setActiveSeason} />
    </nav>
  );
}

interface SeasonsListProps {
  seasons: number[];
  activeSeason: number;
  setActiveSeason: (season: number) => void;
}

function SeasonsList({ seasons, activeSeason, setActiveSeason }: SeasonsListProps) {
  const overflow = useMotionValue('auto');

  return (
    <motion.div className={css.nav} style={{ overflow }}>
      <AnimatePresence initial={false} presenceAffectsLayout>
        {seasons.map((season, index) => (
          <SeasonButton
            key={season}
            index={index}
            season={season}
            activeSeason={activeSeason}
            setActiveSeason={setActiveSeason}
            onLayoutAnimationStart={() => overflow.set('visible')}
            onLayoutAnimationComplete={() => overflow.set('auto')}
          />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

interface SeasonButtonProps extends HTMLMotionProps<'button'> {
  index: number;
  season: number;
  activeSeason: number;
  setActiveSeason: (season: number) => void;
}

function SeasonButton({ index, season, activeSeason, setActiveSeason, ...rest }: SeasonButtonProps) {
  return (
    <motion.button
      type="button"
      className={css.button}
      data-active={activeSeason === season}
      onClick={() => {
        setActiveSeason(season);
      }}
      transition={{ type: 'tween', delay: index * 0.03 }}
      initial={{ opacity: 0, scale: 0.75 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.75 }}
      layout
      {...rest}
    >
      {season}
    </motion.button>
  );
}

export function SeasonSelectorSkeleton({ count = 5 }: { count?: number }) {
  const { t } = useTranslation();

  return (
    <nav className={css.container}>
      <h3 className={css.label}>{t('common:seasons-label')}</h3>
      <div className={css.nav}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className={classes(css.button, css.skeleton)} />
        ))}
      </div>
    </nav>
  );
}
